import { Upload, AlertTriangle } from 'lucide-react';

/**
 * Die Upload-Grenze einer Instanz.
 *
 * ══ WARUM DAS HIER STEHT UND NICHT IM PROXY ════════════════════════════════
 *
 * Der Rahmen um einen Upload ist eine Kette, und sie reißt an ihrem
 * schwächsten Glied:
 *
 *   Browser → NPM (äußerer Proxy) → nginx im Frontend-Image → Anwendung
 *
 * Steht der äußere Proxy niedriger als der Rest, kommt das `413` von dort —
 * und zwar ohne verwertbare Meldung, weil der Proxy die Verbindung kappt,
 * bevor der Browser eine Antwort sieht. Im Browser steht dann nur
 * „Failed to fetch". Genau daran ist der Video-Upload auf sales.juni.cloud
 * gescheitert.
 *
 * Diese Karte setzt das äußere Glied. Von Hand im Proxy-Panel wäre es
 * verloren: `npm-docker-sync` legt den Proxy-Host bei jedem Containerstart aus
 * den Docker-Labels neu an.
 *
 * ══ WAS SIE NICHT IST ══════════════════════════════════════════════════════
 *
 * Keine fachliche Grenze. Wie groß eine einzelne Datei sein darf, entscheidet
 * die Anwendung — bei ojfm je Mandant über `medien_max_mb`. Der Rahmen hier
 * ist die Notbremse darüber, und er soll großzügiger stehen als jede fachliche
 * Grenze, damit die Absage aus der Anwendung kommt und nicht aus dem Proxy.
 */

/** Was die Vorlage einsetzt, wenn hier nichts steht. */
const STANDARD_MB = 2100;
const STANDARD_TIMEOUT_S = 600;

/**
 * Der innere Rahmen im ojfm-Frontend-Image (`frontend/nginx.conf`, ab
 * v1.0.2210). Er ist fest ins Image kopiert und lässt sich von außen nicht
 * setzen — ein höherer Wert hier wirkt deshalb nur bis hierhin.
 */
const INNERER_RAHMEN_MB = 2100;

export default function UploadGrenzeKarte({
  config,
  mbEntwurf,
  timeoutEntwurf,
  setMbEntwurf,
  setTimeoutEntwurf,
  speichern,
  laeuft,
  fehler,
}: {
  config: Record<string, unknown>;
  mbEntwurf: string | null;
  timeoutEntwurf: string | null;
  setMbEntwurf: (w: string | null) => void;
  setTimeoutEntwurf: (w: string | null) => void;
  speichern: (werte: Record<string, string>) => void;
  laeuft: boolean;
  fehler: boolean;
}) {
  const gespeichertMb = typeof config.UPLOAD_MAX_MB === 'string' ? config.UPLOAD_MAX_MB : '';
  const gespeichertTimeout =
    typeof config.UPLOAD_TIMEOUT_S === 'string' ? config.UPLOAD_TIMEOUT_S : '';

  const mb = mbEntwurf ?? gespeichertMb;
  const timeout = timeoutEntwurf ?? gespeichertTimeout;
  const geaendert = mb !== gespeichertMb || timeout !== gespeichertTimeout;

  const mbZahl = Number(mb);
  const ungueltig = mb !== '' && (!Number.isFinite(mbZahl) || mbZahl <= 0);
  const ueberInneremRahmen = Number.isFinite(mbZahl) && mbZahl > INNERER_RAHMEN_MB;

  return (
    <div className="bg-surface border border-slate-200 rounded-xl p-5">
      <div className="flex items-start gap-2">
        <Upload size={15} className="mt-0.5 shrink-0 text-slate-400" />
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Upload-Grenze</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Der Rahmen im Proxy vor dieser Instanz. Nicht die fachliche Grenze — die
            setzt die Anwendung selbst. Leer heißt: {STANDARD_MB} MB und{' '}
            {STANDARD_TIMEOUT_S} s aus der Vorlage.
          </p>
        </div>
      </div>

      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ungueltig) return;
          speichern({ UPLOAD_MAX_MB: mb.trim(), UPLOAD_TIMEOUT_S: timeout.trim() });
        }}
      >
        <label className="block">
          <span className="block text-[11px] font-medium text-slate-600">Größte Datei</span>
          <div className="mt-1 flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              value={mb}
              onChange={(e) => setMbEntwurf(e.target.value)}
              placeholder={String(STANDARD_MB)}
              className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-xs text-slate-500">MB</span>
          </div>
        </label>

        <label className="block">
          <span className="block text-[11px] font-medium text-slate-600">Geduld des Proxys</span>
          <div className="mt-1 flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              value={timeout}
              onChange={(e) => setTimeoutEntwurf(e.target.value)}
              placeholder={String(STANDARD_TIMEOUT_S)}
              className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-xs text-slate-500">Sekunden</span>
          </div>
        </label>

        <button
          type="submit"
          disabled={!geaendert || ungueltig || laeuft}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {laeuft ? 'Speichert…' : 'Speichern'}
        </button>

        {geaendert && !laeuft && (
          <button
            type="button"
            onClick={() => {
              setMbEntwurf(null);
              setTimeoutEntwurf(null);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
          >
            Verwerfen
          </button>
        )}
      </form>

      {ungueltig && (
        <p className="mt-2 text-xs text-red-600">Bitte eine Zahl größer als 0 eintragen.</p>
      )}
      {fehler && <p className="mt-2 text-xs text-red-600">Speichern fehlgeschlagen.</p>}

      {ueberInneremRahmen && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-xs leading-snug text-amber-800">
            Über {INNERER_RAHMEN_MB} MB wirkt dieser Wert nicht allein: das nginx im
            Frontend-Image deckelt dort ebenfalls, und die Konfiguration ist fest ins
            Image kopiert. Größere Uploads brauchen zusätzlich ein Image mit
            angehobener <code className="font-mono">nginx.conf</code>.
          </p>
        </div>
      )}

      <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] leading-snug text-slate-400">
        Wirksam wird der Wert beim nächsten Ausrollen — er reist in der{' '}
        <code className="font-mono">.env</code> des Stacks mit und landet als
        Docker-Label am Container, aus dem der Proxy seinen Eintrag baut.
      </p>
    </div>
  );
}
